import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot } from '@angular/router';
import { RouteHandler } from '@sona/angular-util-router';
import { isFunction } from 'lodash';

import { SUPABASE } from '../../tokens/supabase.token';

/**
 * The options for the auth guard.
 */
export interface IAuthGuardOptions {
    /**
     * A handler that is applied when the user is authenticated. Defaults to `true`.
     *
     * @default `true`
     */
    authenticated?: boolean | RouteHandler;

    /**
     * A handler that is applied when the user is not authenticated. Defaults to `false`.
     *
     * @default `false`
     */
    unauthenticated?: boolean | RouteHandler;
}

/**
 * A guard that checks if the user is authenticated.
 * By default, will allow access to the route if the user is authenticated and deny access if the user is not authenticated.
 *
 * @param options
 */
export const authGuard =
    (options?: IAuthGuardOptions): CanActivateFn =>
    async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
        const supabase = inject(SUPABASE);
        const isAuthenticated =
            (await supabase.auth.getSession()).data?.session?.access_token !=
            null;

        const applyHandler = (handler?: boolean | RouteHandler) =>
            isFunction(handler) ? handler(route, state) : handler;

        if (!isAuthenticated) {
            return applyHandler(options?.unauthenticated) ?? false;
        }

        return applyHandler(options?.authenticated) ?? true;
    };
